class CollectionTabNav extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.staticNav = this.querySelector('[data-nav-static]');
    this.fixedNav = this.querySelector('[data-nav-fixed]');
    this.viewports = Array.from(this.querySelectorAll('.pct__nav-viewport'));
    this.tracks = Array.from(this.querySelectorAll('.pct__nav-track'));
    this.tabs = Array.from(this.querySelectorAll('.pct__tab'));
    this.panels = this.getPanels();
    this.isScrollingToPanel = false;
    this.pendingTarget = null;
    this.activePanelId = null;
    this.isFixedVisible = false;

    if (!this.staticNav || this.tabs.length === 0 || this.panels.length === 0) return;

    this.onTabClick = this.onTabClick.bind(this);
    this.onWindowScroll = this.onWindowScroll.bind(this);
    this.onScrollEnd = this.onScrollEnd.bind(this);
    this.onResize = this.debounce(() => {
      this.syncStickyTop();
      this.syncTrackLayouts();
      this.syncStickyOffset();
      this.updateFixedVisibility();
      if (!this.isScrollingToPanel) this.updateActiveFromScroll();
      this.scrollActiveTabIntoView(false);
    }, 100);

    this.tabs.forEach((tab) => tab.addEventListener('click', this.onTabClick));
    window.addEventListener('scroll', this.onWindowScroll, { passive: true });
    window.addEventListener('scrollend', this.onScrollEnd);
    window.addEventListener('resize', this.onResize);

    this.initStaticObserver();
    this.initTrackObservers();
    this.syncStickyTop();
    this.syncTrackLayouts();
    this.syncStickyOffset();
    this.setActiveTabByTarget(this.tabs[0].getAttribute('data-target'), false);
    this.updateFixedVisibility();
    this.updateActiveFromScroll();
  }

  disconnectedCallback() {
    this.tabs?.forEach((tab) => tab.removeEventListener('click', this.onTabClick));
    window.removeEventListener('scroll', this.onWindowScroll);
    window.removeEventListener('scrollend', this.onScrollEnd);
    window.removeEventListener('resize', this.onResize);
    this.staticObserver?.disconnect();
    this.trackResizeObserver?.disconnect();
    clearTimeout(this.scrollLockTimer);
    clearTimeout(this.scrollSettleTimer);
  }

  initTrackObservers() {
    this.trackResizeObserver = new ResizeObserver(() => {
      this.syncTrackLayouts();
      this.syncStickyOffset();
    });

    this.viewports.forEach((viewport) => this.trackResizeObserver.observe(viewport));
    this.tracks.forEach((track) => this.trackResizeObserver.observe(track));
  }

  syncTrackLayouts() {
    this.viewports.forEach((viewport) => {
      const track = viewport.querySelector('.pct__nav-track');
      if (!track) return;

      const tabCount = track.querySelectorAll('.pct__tab').length;
      const overflow = track.scrollWidth > viewport.clientWidth + 1;
      const shouldCenter = !overflow && tabCount < 3;

      viewport.classList.toggle('is-overflowing', overflow);
      viewport.classList.toggle('is-centered', shouldCenter);

      if (!overflow) {
        viewport.scrollLeft = 0;
      }
    });

    const anyOverflow = this.viewports.some((viewport) => viewport.classList.contains('is-overflowing'));
    const anyCentered = this.viewports.some((viewport) => viewport.classList.contains('is-centered'));
    this.classList.toggle('is-overflowing', anyOverflow);
    this.classList.toggle('is-centered', anyCentered);
  }

  getPanels() {
    const root = this.closest('.pct');
    if (!root) return [];
    return Array.from(root.querySelectorAll('.pct__panel[id^="pct-panel-"]'));
  }

  getHeaderOffset() {
    const styles = getComputedStyle(document.documentElement);
    const groupHeight = parseFloat(styles.getPropertyValue('--header-group-height')) || 0;
    if (groupHeight > 0) return groupHeight;

    const headerHeight = parseFloat(styles.getPropertyValue('--header-height')) || 0;
    const announcementHeight = parseFloat(styles.getPropertyValue('--announcement-bar-height')) || 0;
    return headerHeight + announcementHeight;
  }

  syncStickyTop() {
    const top = this.getHeaderOffset();
    this.style.setProperty('--pct-sticky-top', `${top}px`);
  }

  /** Fixed bar height is measurable even while hidden (opacity/transform only). */
  getFixedNavHeight() {
    return this.fixedNav?.offsetHeight || 0;
  }

  getStaticNavHeight() {
    return this.staticNav?.offsetHeight || 0;
  }

  getActiveNavHeight() {
    if (this.isFixedVisible) return this.getFixedNavHeight() || this.getStaticNavHeight();
    return this.getStaticNavHeight();
  }

  /**
   * Predict sticky offset for a destination panel.
   * After leaving the hero tabs, the fixed text bar is what covers the content —
   * using the wrong height (static image tabs vs fixed text tabs) lands ~row-gap off
   * and the scroll-spy then activates the wrong tab.
   */
  getScrollOffsetForPanel(panel) {
    const headerOffset = this.getHeaderOffset();
    const fixedHeight = this.getFixedNavHeight();
    const staticHeight = this.getStaticNavHeight();
    const buffer = 8;

    if (!panel || !this.staticNav) {
      return headerOffset + (this.isFixedVisible ? fixedHeight : staticHeight) + buffer;
    }

    const staticRect = this.staticNav.getBoundingClientRect();
    const staticBottomDoc = staticRect.bottom + window.scrollY;
    const panelTopDoc = panel.getBoundingClientRect().top + window.scrollY;

    // If the panel sits below the in-flow tabs, the fixed bar will be showing.
    const willShowFixed = panelTopDoc > staticBottomDoc - headerOffset;
    const navHeight = willShowFixed
      ? fixedHeight || staticHeight
      : staticHeight || fixedHeight;

    return headerOffset + navHeight + buffer;
  }

  getScrollOffset() {
    return this.getHeaderOffset() + this.getActiveNavHeight() + 8;
  }

  syncStickyOffset() {
    document.documentElement.style.setProperty(
      '--pct-scroll-offset',
      `${Math.max(0, this.getScrollOffset())}px`
    );
  }

  initStaticObserver() {
    if (!this.staticNav) return;

    this.staticObserver = new IntersectionObserver(
      ([entry]) => {
        this.setFixedVisible(!entry.isIntersecting);
      },
      {
        threshold: [0, 0.01, 1],
        rootMargin: `-${this.getHeaderOffset()}px 0px 0px 0px`
      }
    );

    this.staticObserver.observe(this.staticNav);
  }

  setFixedVisible(visible) {
    if (this.isFixedVisible === visible) {
      this.syncStickyOffset();
      return;
    }

    this.isFixedVisible = visible;
    this.classList.toggle('is-stuck', visible);
    this.fixedNav?.classList.toggle('is-visible', visible);
    this.fixedNav?.setAttribute('aria-hidden', visible ? 'false' : 'true');
    this.syncStickyOffset();
  }

  updateFixedVisibility() {
    if (!this.staticNav) return;

    const rect = this.staticNav.getBoundingClientRect();
    const headerOffset = this.getHeaderOffset();
    const outOfView = rect.bottom <= headerOffset + 1;
    this.setFixedVisible(outOfView);
  }

  onWindowScroll() {
    this.updateFixedVisibility();
    if (this.isScrollingToPanel) return;
    this.updateActiveFromScroll();
  }

  onScrollEnd() {
    if (!this.isScrollingToPanel) return;
    this.finishProgrammaticScroll();
  }

  updateActiveFromScroll() {
    if (!this.panels.length || this.isScrollingToPanel) return;

    const offset = this.getScrollOffset();
    const probe = offset + 4;
    let activePanel = this.panels[0];

    for (const panel of this.panels) {
      const top = panel.getBoundingClientRect().top;
      if (top <= probe) {
        activePanel = panel;
      }
    }

    const target = `#${activePanel.id}`;
    if (this.activePanelId === activePanel.id) return;

    this.setActiveTabByTarget(target, true);
  }

  onTabClick(event) {
    event.preventDefault();

    const tab = event.currentTarget;
    const targetSelector = tab.getAttribute('data-target');
    const panel = targetSelector ? document.querySelector(targetSelector) : null;

    if (!panel) return;

    this.pendingTarget = targetSelector;
    this.lockScrollSpy();
    this.setActiveTabByTarget(targetSelector, true);
    this.scrollToPanel(panel);
  }

  lockScrollSpy() {
    this.isScrollingToPanel = true;
    clearTimeout(this.scrollLockTimer);
    clearTimeout(this.scrollSettleTimer);

    // Fallback when scrollend is unavailable or never fires
    this.scrollLockTimer = setTimeout(() => {
      this.finishProgrammaticScroll();
    }, 1200);
  }

  finishProgrammaticScroll() {
    if (!this.isScrollingToPanel && !this.pendingTarget) return;

    clearTimeout(this.scrollLockTimer);
    clearTimeout(this.scrollSettleTimer);

    // Let fixed-bar visibility + sticky offset settle after the scroll
    this.scrollSettleTimer = setTimeout(() => {
      this.updateFixedVisibility();
      this.syncStickyOffset();

      if (this.pendingTarget) {
        this.setActiveTabByTarget(this.pendingTarget, true);
        this.pendingTarget = null;
      }

      this.isScrollingToPanel = false;
    }, 60);
  }

  scrollToPanel(panel) {
    const offset = this.getScrollOffsetForPanel(panel);
    const top = panel.getBoundingClientRect().top + window.scrollY - offset;
    const nextTop = Math.max(0, Math.round(top));

    // Already aligned — still keep the clicked tab active
    if (Math.abs(window.scrollY - nextTop) < 2) {
      this.finishProgrammaticScroll();
      return;
    }

    window.scrollTo({ top: nextTop, behavior: 'smooth' });
  }

  setActiveTabByTarget(targetSelector, scrollTabIntoView) {
    if (!targetSelector) return;

    this.tabs.forEach((tab) => {
      const isActive = tab.getAttribute('data-target') === targetSelector;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.tabIndex = isActive ? 0 : -1;
    });

    this.activePanelId = targetSelector.replace('#', '');

    if (scrollTabIntoView) {
      this.scrollActiveTabIntoView(true);
    }
  }

  scrollActiveTabIntoView(smooth) {
    this.viewports.forEach((viewport) => {
      const track = viewport.querySelector('.pct__nav-track');
      const activeTab = viewport.querySelector('.pct__tab.is-active');
      if (!track || !activeTab) return;

      const overflow = track.scrollWidth > viewport.clientWidth + 1;
      if (!overflow) return;

      const viewportRect = viewport.getBoundingClientRect();
      const tabRect = activeTab.getBoundingClientRect();

      if (tabRect.left < viewportRect.left + 8 || tabRect.right > viewportRect.right - 8) {
        const scrollLeft =
          activeTab.offsetLeft - viewport.clientWidth / 2 + activeTab.offsetWidth / 2;

        viewport.scrollTo({
          left: Math.max(0, scrollLeft),
          behavior: smooth ? 'smooth' : 'auto'
        });
      }
    });
  }

  debounce(fn, wait) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

if (!customElements.get('collection-tab-nav')) {
  customElements.define('collection-tab-nav', CollectionTabNav);
}
